import { Node, Plugin } from "tiptap";
import { nodeInputRule } from "tiptap-commands";
import ResizableImageView from "./ResizableImageView.vue";
import { uploadRichTextImage } from "./uploadRichTextImage";

const IMAGE_INPUT_REGEX = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/;

function insertUploadedImages(view, files, insertPos) {
  const images = Array.from(files || []).filter((file) => /image/i.test(file.type));
  if (!images.length) return false;

  const { schema } = view.state;
  images.forEach((image) => {
    uploadRichTextImage(image)
      .then(({ src, alt }) => {
        const node = schema.nodes.image.create({
          src,
          alt: alt || image.name,
          width: "50%",
        });
        const pos = typeof insertPos === "function" ? insertPos() : insertPos;
        const transaction = view.state.tr.insert(pos, node);
        view.dispatch(transaction);
      })
      .catch((err) => {
        console.error("Image upload failed:", err);
      });
  });

  return true;
}

function normalizeWidth(value) {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+(\.\d+)?%$/.test(text)) return text;
  if (/^\d+(\.\d+)?px$/.test(text)) return text;
  if (/^\d+(\.\d+)?$/.test(text)) return `${text}px`;
  return text;
}

export default class ResizableImageNode extends Node {
  get name() {
    return "image";
  }

  get view() {
    return ResizableImageView;
  }

  get schema() {
    return {
      inline: true,
      attrs: {
        src: {},
        alt: {
          default: null,
        },
        title: {
          default: null,
        },
        width: {
          default: null,
        },
      },
      group: "inline",
      draggable: true,
      parseDOM: [
        {
          tag: "img[src]",
          getAttrs: (dom) => ({
            src: dom.getAttribute("src"),
            title: dom.getAttribute("title"),
            alt: dom.getAttribute("alt"),
            width: normalizeWidth(dom.getAttribute("width") || dom.style.width || null),
          }),
        },
      ],
      toDOM: (node) => {
        const attrs = {
          src: node.attrs.src,
        };
        if (node.attrs.alt) attrs.alt = node.attrs.alt;
        if (node.attrs.title) attrs.title = node.attrs.title;
        const width = normalizeWidth(node.attrs.width);
        if (width) {
          attrs.style = `width: ${width}; max-width: 100%; height: auto; display: inline-block; vertical-align: top;`;
        } else {
          attrs.style = "max-width: 100%; height: auto; display: inline-block; vertical-align: top;";
        }
        return ["img", attrs];
      },
    };
  }

  commands({ type }) {
    return (attrs) => (state, dispatch) => {
      const { selection } = state;
      const position = selection.$cursor ? selection.$cursor.pos : selection.$to.pos;
      const node = type.create({
        ...attrs,
        width: normalizeWidth(attrs && attrs.width) || attrs.width || null,
      });
      const transaction = state.tr.insert(position, node);
      dispatch(transaction);
    };
  }

  inputRules({ type }) {
    return [
      nodeInputRule(IMAGE_INPUT_REGEX, type, (match) => {
        const [, alt, src, title] = match;
        return {
          src,
          alt,
          title,
        };
      }),
    ];
  }

  get plugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            drop(view, event) {
              const files = event.dataTransfer && event.dataTransfer.files;
              if (!files || !files.length) return false;

              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              if (!coordinates) return false;

              const handled = insertUploadedImages(view, files, coordinates.pos);
              if (handled) event.preventDefault();
              return handled;
            },
            paste(view, event) {
              const files = event.clipboardData && event.clipboardData.files;
              if (!files || !files.length) return false;

              const { selection } = view.state;
              const pos = selection.$cursor ? selection.$cursor.pos : selection.$to.pos;
              const handled = insertUploadedImages(view, files, pos);
              if (handled) event.preventDefault();
              return handled;
            },
          },
        },
      }),
    ];
  }
}
